// Grupos view — full CRUD + invite + leave + delete against /api/groups.
// No DOM access after unmount/scope-change; every async path uses
// AbortSignal to cancel in-flight reads and selection changes.
import { h, toast, confirmModal, formModal, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import type { ApiClient } from '../../api/client.js';
import type { Group, GroupDetail, GroupMember } from '../../api/people-types.js';

const MAX_NAME = 64;
const MIN_NAME = 1;
const MEMBER_LIMIT = 25;

type GroupsHost = {
  api(): ApiClient;
  userId: string;
  navigate(section: string, scope?: { kind: 'group'; id: string; label: string }): void;
};

export function renderGrupos(host: GroupsHost): View {
  const section = document.createElement('section');
  section.className = 'view view-grupos';
  section.setAttribute('aria-label', 'Grupos');

  const header = renderHeader('grupos');
  section.append(header);

  const toolbar = h('div', { class: 'list-toolbar' });
  const btnCreate = h('button', { class: 'btn primary', type: 'button' }, ['Crear grupo']);
  const btnJoin = h('button', { class: 'btn ghost', type: 'button' }, ['Unirme con código']);
  toolbar.append(
    h('h3', { class: 'list-toolbar-title' }, ['Mis grupos']),
    h('div', { class: 'list-toolbar-actions' }, [btnCreate, btnJoin]),
  );
  section.append(toolbar);

  const listPanel = h('section', { class: 'panel list-panel', 'aria-label': 'Lista de grupos' });
  const listStatus = h('p', { class: 'panel-status muted-text', role: 'status' });
  const list = h('ul', { class: 'list tokens', role: 'list' });
  listPanel.append(listStatus, list);
  const layout = h('div', {class:'section-body split groups-layout'}, [listPanel]);
  section.append(layout);

  const detailPanel = h('section', { class: 'panel detail-panel', 'aria-label': 'Detalle del grupo' });
  layout.append(detailPanel);

  let alive = true;
  let listAbort: AbortController | null = null;
  let selectAbort: AbortController | null = null;
  let activeGroups: Group[] = [];
  let selectedId: string | null = null;
  let selectedDetail: GroupDetail | null = null;

  const guard = (): boolean => alive && section.isConnected;

  function renderList(groups: Group[]) {
    activeGroups = groups;
    list.replaceChildren();
    if (groups.length === 0) {
      listStatus.textContent = '';
      list.append(h('li', { class: 'empty' }, [h('h3', {}, ['Un espacio para compartir']), h('p', {}, ['Aún no tienes grupos. Crea uno o únete con un código. Puedes hablar con ASIS para saber por dónde empezar.']), h('a', { class: 'btn ghost', href: '#/conversar' }, ['Hablar con ASIS'])]));
      return;
    }
    listStatus.textContent = '';
    for (const g of groups) {
      const role = g.role === 'owner' ? 'Propietario' : 'Miembro';
      const li = h('li', {
        class: `list-item token${selectedId === g.id ? ' selected' : ''}`,
        role: 'button',
        tabindex: '0',
        'aria-pressed': selectedId === g.id ? 'true' : 'false',
        dataset: { groupId: g.id },
      }, [
        h('span', { class: 'token-title' }, [g.name]),
        h('span', { class: 'token-meta' }, [`${role} · ${g.memberCount}/${MEMBER_LIMIT} miembros`]),
      ]);
      li.addEventListener('click', () => selectGroup(g.id));
      li.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectGroup(g.id); }
      });
      list.append(li);
    }
  }

  function renderListError(msg: string) {
    list.replaceChildren();
    listStatus.textContent = '';
    list.append(h('li', { class: 'list-item error', role: 'alert' }, [msg]));
  }

  function renderListLoading() {
    list.replaceChildren();
    listStatus.textContent = 'Cargando grupos…';
    list.append(h('li', { class: 'list-item placeholder muted-text' }, ['Cargando…']));
  }

  function renderDetailLoading(name: string) {
    detailPanel.replaceChildren(
      h('header', { class: 'panel-header' }, [
        h('h3', { class: 'panel-title' }, [name]),
      ]),
      h('p', { class: 'muted-text' }, ['Cargando detalle…']),
    );
  }

  function renderDetail(detail: GroupDetail) {
    const g = detail.group;
    const isOwner = g.role === 'owner';
    const full = g.memberCount >= MEMBER_LIMIT;
    const ownerMember = detail.members.find(m => m.role === 'owner');
    const head = h('header', { class: 'panel-header' }, [
      h('h3', { class: 'panel-title' }, [g.name]),
      h('p', { class: 'panel-sub' }, [`${detail.members.length}/${MEMBER_LIMIT} miembros · ${isOwner ? 'Eres propietario' : 'Eres miembro'}`]),
    ]);
    const memberList = h('ul', { class: 'list tokens members', role: 'list' });
    for (const m of detail.members) {
      memberList.append(renderMemberRow(g.id, m, isOwner, m.userId === ownerMember?.userId));
    }

    const invitePanel = h('section', { class: 'card' }, [
      h('h4', {}, ['Invitar']),
      h('p', { class: 'muted-text' }, [
        'Genera un código de un solo uso (24h, sin emails). Compártelo con quien vaya a unirse.',
      ]),
      h('div', { class: 'inline-actions' }, [renderInviteControls(g, isOwner, full)]),
    ]);

    const ownerPanel = isOwner
      ? h('section', { class: 'card danger-zone' }, [
          h('h4', {}, ['Eliminar grupo']),
          h('p', { class: 'muted-text' }, ['Borra el grupo para todos sus miembros. Tendrás que escribir BORRAR.']),
          h('button', { class: 'btn danger', type: 'button' }, ['Eliminar grupo…']),
        ])
      : h('section', { class: 'card' }, [
          h('h4', {}, ['Salir del grupo']),
          h('p', { class: 'muted-text' }, ['Deja de participar en este grupo. El propietario mantiene el control.']),
          h('button', { class: 'btn ghost', type: 'button' }, ['Salir del grupo…']),
        ]);

    detailPanel.replaceChildren(head, h('button', {class:'btn ghost go-knowledge',type:'button'}, ['Abrir conocimiento del grupo']), memberList, invitePanel, ownerPanel);
    detailPanel.querySelectorAll<HTMLButtonElement>('button').forEach(b => wireOwnerButtons(b, g));
    const knowledgeBtn = detailPanel.querySelector<HTMLButtonElement>('button.go-knowledge');
    if (knowledgeBtn) {
      knowledgeBtn.addEventListener('click', () => {
        if (!guard()) return;
        host.navigate('conocimiento', { kind: 'group', id: g.id, label: g.name });
      });
    }
  }

  function renderInviteControls(g: Group, isOwner: boolean, full: boolean): HTMLElement {
    if (!isOwner) {
      return h('p', { class: 'muted-text' }, ['Solo el propietario puede generar códigos de invitación.']);
    }
    const btn = h('button', { class: 'btn primary', type: 'button' }, ['Generar código']);
    const out = h('div', { class: 'invite-out' });
    if (full) {
      btn.disabled = true;
      btn.title = 'Grupo completo (25/25)';
      out.append(h('p', { class: 'muted-text' }, ['Grupo completo, no admite más miembros.']));
    }
    btn.addEventListener('click', async () => {
      if (!guard() || btn.disabled) return;
      btn.disabled = true; const old = btn.textContent; btn.textContent = 'Generando…';
      const ac = new AbortController();
      selectAbort?.abort(); selectAbort = ac;
      try {
        const inv = await host.api().inviteGroup(g.id, ac.signal);
        if (!guard() || ac.signal.aborted || selectedId !== g.id) return;
        out.replaceChildren();
        out.append(
          h('p', { class: 'invite-line' }, [
            'Código: ',
            h('code', { class: 'invite-code' }, [inv.code]),
            h('span', { class: 'muted-text' }, [` · caduca ${formatExpiry(inv.expiresAt)}`]),
          ]),
          h('p', { class: 'invite-hint' }, ['Un solo uso, 24h, no se envía por email. Compártelo en persona.']),
          h('div', { class: 'inline-actions' }, [
            (() => {
                const copy = h('button', { class: 'btn ghost', type: 'button' }, ['Copiar']);
                copy.addEventListener('click', async () => {
                  if (!guard()) return;
                  try { await navigator.clipboard.writeText(inv.code); if (guard()) toast('Código copiado', 'ok'); }
                  catch { if (guard()) toast('No se pudo copiar', 'error'); }
                });
                return copy;
              })(),
          ]),
        );
      } catch (err) {
        if (!guard()) return;
        if (!ac.signal.aborted) toast((err as Error).message || 'No se pudo generar el código', 'error');
      } finally {
        btn.disabled = false; btn.textContent = old ?? 'Generar código';
      }
    });
    const wrap = h('div', { class: 'invite-controls' }, [btn, out]);
    return wrap;
  }

  function renderMemberRow(groupId: string, m: GroupMember, isOwner: boolean, isOwnerRow: boolean): HTMLElement {
    const row = h('li', { class: 'list-item token member-row', dataset: { userId: m.userId } }, [
      h('span', { class: 'token-title' }, [m.displayName + (isOwnerRow ? ' (propietario)' : '')]),
      h('span', { class: 'token-meta' }, [m.role === 'owner' ? 'Propietario' : `Miembro desde ${new Date(m.joinedAt).toLocaleDateString()}`]),
    ]);
    if (isOwner && !isOwnerRow) {
      const btn = h('button', { class: 'btn ghost small', type: 'button' }, ['Quitar']);
      btn.addEventListener('click', async () => {
        if (!guard()) return;
        const ok = await confirmModal({
          title: 'Quitar miembro',
          body: `¿Quitar a ${m.displayName} del grupo?`,
          confirmLabel: 'Quitar',
          danger: true,
        });
        if (!ok || !guard() || selectedId !== groupId) return;
        btn.disabled = true;
        const ac = new AbortController();
        selectAbort?.abort(); selectAbort = ac;
        try {
          await host.api().removeGroupMember(groupId, m.userId, ac.signal);
          if (!guard() || ac.signal.aborted || selectedId !== groupId) return;
          toast('Miembro eliminado', 'ok');
          await loadDetail(groupId);
        } catch (err) {
          if (!guard()) return;
          if (!ac.signal.aborted) toast((err as Error).message || 'No se pudo quitar al miembro', 'error');
        } finally { if (guard()) btn.disabled = false; }
      });
      row.append(h('div', { class: 'row-actions' }, [btn]));
    }
    return row;
  }

  function wireOwnerButtons(btn: HTMLButtonElement, g: Group) {
    if (g.role === 'owner' && btn.textContent === 'Eliminar grupo…') {
      btn.addEventListener('click', async () => {
        if (!guard()) return;
        const ok = await formModal({
          title: 'Eliminar grupo',
          body: `Vas a borrar “${g.name}” para todos sus miembros. Escribe BORRAR para confirmar.`,
          submitLabel: 'Eliminar',
          danger: true,
          fields: [{ name: 'confirm', label: 'Escribe BORRAR' }],
          onSave: async (vals) => {
        if (!guard()) throw new Error('Esta vista ya se ha cerrado.');
            if (!guard() || selectedId !== g.id) throw new Error('La selección ha cambiado. Abre de nuevo la acción.');
            const v = vals.confirm;
            if (v !== 'BORRAR') throw new Error('Debes escribir BORRAR exactamente');
            const ac = new AbortController();
            selectAbort?.abort(); selectAbort = ac;
            await host.api().deleteGroup(g.id, { confirm: 'BORRAR' }, ac.signal);
          },
        });
        if (!ok || !guard()) return;
        toast('Grupo eliminado', 'ok');
        selectedId = null; selectedDetail = null;
        detailPanel.replaceChildren();
        await loadList();
      });
    } else if (g.role !== 'owner' && btn.textContent === 'Salir del grupo…') {
      btn.addEventListener('click', async () => {
        if (!guard()) return;
        const ok = await confirmModal({
          title: 'Salir del grupo',
          body: `¿Salir de “${g.name}”?`,
          confirmLabel: 'Salir',
          danger: true,
        });
        if (!ok || !guard()) return;
        const ac = new AbortController();
        selectAbort?.abort(); selectAbort = ac;
        try {
          await host.api().removeGroupMember(g.id, host.userId, ac.signal);
          if (!guard()) return;
          toast('Has salido del grupo', 'ok');
          selectedId = null; selectedDetail = null;
          detailPanel.replaceChildren();
          await loadList();
        } catch (err) {
          if (!guard()) return;
          toast((err as Error).message || 'No se pudo salir', 'error');
        }
      });
    }
  }

  async function loadList() {
    listAbort?.abort();
    const ac = new AbortController();
    listAbort = ac;
    renderListLoading();
    try {
      const res = await host.api().listGroups(ac.signal);
      if (!guard() || ac.signal.aborted) return;
      renderList(res.groups);
      if (selectedId && !res.groups.some(g => g.id === selectedId)) {
        selectedId = null; selectedDetail = null; detailPanel.replaceChildren();
      } else if (selectedId) {
        const g = res.groups.find(x => x.id === selectedId);
        if (g) await loadDetail(g.id);
      }
    } catch (err) {
      if (!guard() || ac.signal.aborted) return;
      renderListError((err as Error).message || 'No se pudieron cargar los grupos');
    }
  }

  async function loadDetail(id: string) {
    selectAbort?.abort();
    const ac = new AbortController();
    selectAbort = ac;
    const placeholder = activeGroups.find(g => g.id === id);
    renderDetailLoading(placeholder?.name ?? 'Grupo');
    try {
      const detail = await host.api().getGroup(id, ac.signal);
      if (!guard() || ac.signal.aborted) return;
      selectedId = id; selectedDetail = detail;
      renderList(activeGroups);
      renderDetail(detail);
    } catch (err) {
      if (!guard() || ac.signal.aborted) return;
      detailPanel.replaceChildren(h('p', { class: 'auth-error', role: 'alert' }, [(err as Error).message || 'No se pudo cargar el detalle']));
    }
  }

  async function selectGroup(id: string) {
    if (id === selectedId && selectedDetail) {
      renderList(activeGroups);
      return;
    }
    selectedId = id; selectedDetail = null;
    renderList(activeGroups);
    await loadDetail(id);
  }

  btnCreate.addEventListener('click', async () => {
    if (!guard()) return;
    let lastName = '';
    const ok = await formModal({
      title: 'Crear grupo',
      body: 'Elige un nombre para el grupo (1–64 caracteres).',
      submitLabel: 'Crear',
      fields: [{ name: 'name', label: 'Nombre del grupo' }],
      onSave: async (vals) => {
        if (!guard()) throw new Error('Esta vista ya se ha cerrado.');
        const name = (vals.name ?? '').trim();
        if (name.length < MIN_NAME || name.length > MAX_NAME) throw new Error(`Nombre entre ${MIN_NAME} y ${MAX_NAME} caracteres`);
        lastName = name;
        const ac = new AbortController();
        selectAbort?.abort(); selectAbort = ac;
        const res = await host.api().createGroup({ name }, ac.signal);
        if (!guard()) return;
        toast('Grupo creado', 'ok');
        await loadList();
        await loadDetail(res.group.id);
      },
    });
    void ok; void lastName;
  });

  btnJoin.addEventListener('click', async () => {
    if (!guard()) return;
    let lastCode = '';
    const ok = await formModal({
      title: 'Unirme a un grupo',
      body: 'Pega el código de un solo uso que te hayan compartido.',
      submitLabel: 'Unirme',
      fields: [{ name: 'code', label: 'Código de invitación' }],
      onSave: async (vals) => {
        if (!guard()) throw new Error('Esta vista ya se ha cerrado.');
        const code = (vals.code ?? '').trim();
        if (!code) throw new Error('Código obligatorio');
        lastCode = code;
        const ac = new AbortController();
        selectAbort?.abort(); selectAbort = ac;
        const res = await host.api().joinGroup({ code }, ac.signal);
        if (!guard()) return;
        toast('Te has unido al grupo', 'ok');
        await loadList();
        await loadDetail(res.group.id);
      },
    });
    void ok; void lastCode;
  });

  void loadList();

  return {
    el: section,
    destroy() {
      alive = false;
      listAbort?.abort();
      selectAbort?.abort();
    },
  };
}

function formatExpiry(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}